import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { useAlert } from '../contexts/AlertContext'
import { loadPropertyMoveTickets, updatePropertyMoveTicketStatus } from '../lib/apiPhpAdapter'
import { Ticket } from '../types'
import PropertyMoveTicketList from '../components/PropertyMoveTicketList'
import Pagination from '../components/Pagination'
import { useIsMobile } from '../hooks/useMediaQuery'
import './PropertyReferentView.css'

function PropertyReferentView() {
  const { user } = useAuth()
  const { showAlert } = useAlert()
  const [loading, setLoading] = useState(true)
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100

  useEffect(() => {
    loadTicketsData()
  }, [user])

  const loadTicketsData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      const propertyTickets = await loadPropertyMoveTickets()
      setTickets(propertyTickets)
    } catch (error) {
      console.error('Chyba při načítání žádostí:', error)
      alert('Chyba při načítání žádostí')
    } finally {
      setLoading(false)
    }
  }

  useDeepLinkById({
    items: tickets,
    ready: !loading,
    onFound: (t) => {
      setStatusFilter(t.status)
    },
    onNotFound: () => showAlert('Žádost nenalezena nebo již není ve vaší frontě.', 'error'),
    elementIdFor: (id) => `ticket-${id}`,
  })

  // Фильтрация по статусу и поиску
  const filteredTickets = useMemo(() => {
    let filtered = tickets
    
    // Фильтр по статусу
    if (statusFilter) {
      filtered = filtered.filter(t => {
        if (statusFilter === 'new') return t.status === 'new'
        if (statusFilter === 'in_progress') return t.status === 'in_progress' || t.status === 'recipient_needs_transport' || t.status === 'recipient_accepted_self_transport' || t.status === 'awaiting_transport'
        if (statusFilter === 'completed') return (t.status === 'completed' || t.status === 'rejected') && !t.processed_at
        if (statusFilter === 'rejected') return t.status === 'rejected' && !t.processed_at
        if (statusFilter === 'processed') return t.processed_at !== null && t.processed_at !== undefined
        return true
      })
    }
    
    // Фильтрация по поиску
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim()
      filtered = filtered.filter(t => {
        const inventoryNumber = (t.inventory_number || '').toLowerCase()
        const itemName = (t.item_name || '').toLowerCase()
        const fromLocation = (t.from_location || '').toLowerCase()
        const toLocation = (t.to_location || '').toLowerCase()
        const requesterName = (t.requester_name || '').toLowerCase()
        const assignedToName = (t.assigned_to_name || '').toLowerCase()
        const fromBuildingName = (t.from_building_name || '').toLowerCase()
        const toBuildingName = (t.to_building_name || '').toLowerCase()
        const status = (t.status || '').toLowerCase()
        const rejectionReason = ((t as any).rejection_reason || '').toLowerCase()
        
        return inventoryNumber.includes(searchLower) ||
               itemName.includes(searchLower) ||
               fromLocation.includes(searchLower) ||
               toLocation.includes(searchLower) ||
               requesterName.includes(searchLower) ||
               assignedToName.includes(searchLower) ||
               fromBuildingName.includes(searchLower) ||
               toBuildingName.includes(searchLower) ||
               status.includes(searchLower) ||
               rejectionReason.includes(searchLower)
      })
    }
    
    return filtered
  }, [tickets, statusFilter, searchText])
  
  // Группируем заявки по статусам для статистики (используем все tickets, не filteredTickets)
  const ticketsForStats = useMemo(() => {
    if (!searchText.trim()) return tickets
    
    const searchLower = searchText.toLowerCase().trim()
    return tickets.filter(t => {
      const inventoryNumber = (t.inventory_number || '').toLowerCase()
      const itemName = (t.item_name || '').toLowerCase()
      const fromLocation = (t.from_location || '').toLowerCase()
      const toLocation = (t.to_location || '').toLowerCase()
      const requesterName = (t.requester_name || '').toLowerCase()
      const assignedToName = (t.assigned_to_name || '').toLowerCase()
      const fromBuildingName = (t.from_building_name || '').toLowerCase()
      const toBuildingName = (t.to_building_name || '').toLowerCase()
      const status = (t.status || '').toLowerCase()
        const rejectionReason = ((t as any).rejection_reason || '').toLowerCase()
      
      return inventoryNumber.includes(searchLower) ||
             itemName.includes(searchLower) ||
             fromLocation.includes(searchLower) ||
             toLocation.includes(searchLower) ||
             requesterName.includes(searchLower) ||
             assignedToName.includes(searchLower) ||
             fromBuildingName.includes(searchLower) ||
             toBuildingName.includes(searchLower) ||
             status.includes(searchLower) ||
             rejectionReason.includes(searchLower)
    })
  }, [tickets, searchText])

  const newTickets = useMemo(() => ticketsForStats.filter(t => t.status === 'new'), [ticketsForStats])
  const inProgressTickets = useMemo(() => ticketsForStats.filter(t => t.status === 'in_progress' || t.status === 'recipient_needs_transport' || t.status === 'recipient_accepted_self_transport' || t.status === 'awaiting_transport'), [ticketsForStats])
  const completedTickets = useMemo(() => ticketsForStats.filter(t => t.status === 'completed' && !t.processed_at), [ticketsForStats])
  const rejectedTickets = useMemo(() => ticketsForStats.filter(t => t.status === 'rejected' && !t.processed_at), [ticketsForStats])
  const processedTickets = useMemo(() => ticketsForStats.filter(t => t.processed_at), [ticketsForStats])

  // Группируем отфильтрованные заявки для отображения
  const filteredNewTickets = useMemo(() => filteredTickets.filter(t => t.status === 'new'), [filteredTickets])
  const filteredInProgressTickets = useMemo(() => filteredTickets.filter(t => t.status === 'in_progress' || t.status === 'recipient_needs_transport' || t.status === 'recipient_accepted_self_transport' || t.status === 'awaiting_transport'), [filteredTickets])
  const filteredCompletedTickets = useMemo(() => filteredTickets.filter(t => t.status === 'completed' && !t.processed_at), [filteredTickets])
  const filteredRejectedTickets = useMemo(() => filteredTickets.filter(t => t.status === 'rejected' && !t.processed_at), [filteredTickets])
  const filteredProcessedTickets = useMemo(() => filteredTickets.filter(t => t.processed_at), [filteredTickets])

  // Пагинация
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage)
  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredTickets.slice(startIndex, endIndex)
  }, [filteredTickets, currentPage, itemsPerPage])

  // Сброс страницы при изменении фильтра
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, searchText])

  const handleStatusChange = async (ticketId: string, newStatus: 'new' | 'in_progress' | 'completed' | 'organize_transport') => {
    try {
      await updatePropertyMoveTicketStatus(ticketId, newStatus)
      await loadTicketsData()
    } catch (error) {
      console.error('Chyba při aktualizaci statusu:', error)
      alert('Chyba při aktualizaci statusu')
    }
  }

  if (loading) {
    return <div className="loading">Načítání...</div>
  }

  return (
    <div className="property-referent-view">
      <h2>Referent správy majetku - Všechny žádosti</h2>
      
      {tickets.length === 0 ? (
        <div className="no-tickets">
          <p>Žádné žádosti o přesun majetku</p>
        </div>
      ) : (
        <>
          {/* Поиск */}
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Hledat v žádostech..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-input"
              style={{ flex: 1, minWidth: '200px', maxWidth: '400px', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Статистика */}
          <div className="admin-stats" style={{ marginBottom: '2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <div 
              className={`stat-card ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'new' ? null : 'new')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Nové</h3>
              <p className="stat-number">{newTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
              style={{ cursor: 'pointer' }}
            >
              <h3>V práci</h3>
              <p className="stat-number">{inProgressTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'rejected' ? null : 'rejected')}
              style={{ cursor: 'pointer', backgroundColor: statusFilter === 'rejected' ? '#fee2e2' : undefined, borderColor: statusFilter === 'rejected' ? '#ef4444' : undefined }}
            >
              <h3>Zamítnuto</h3>
              <p className="stat-number">{rejectedTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'completed' ? null : 'completed')}
              style={{ cursor: 'pointer' }}
            >
              <h3>Vyřízeno</h3>
              <p className="stat-number">{completedTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === 'processed' ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'processed' ? null : 'processed')}
              style={{ cursor: 'pointer', backgroundColor: statusFilter === 'processed' ? '#d1fae5' : undefined, borderColor: statusFilter === 'processed' ? '#10b981' : undefined }}
            >
              <h3>Zpracováno</h3>
              <p className="stat-number">{processedTickets.length}</p>
            </div>
            <div 
              className={`stat-card ${statusFilter === null ? 'active' : ''}`}
              onClick={() => setStatusFilter(null)}
              style={{ cursor: 'pointer', backgroundColor: statusFilter === null ? '#0d9488' : undefined, color: statusFilter === null ? 'white' : undefined }}
            >
              <h3>Celkem</h3>
              <p className="stat-number">{ticketsForStats.length}</p>
            </div>
          </div>
          
          {/* Секции заявок */}
          {statusFilter ? (
            <div className="tickets-section">
              <h2>
                {statusFilter === 'new' && `Nové žádosti (${filteredNewTickets.length})`}
                {statusFilter === 'in_progress' && `V práci (${filteredInProgressTickets.length})`}
                {statusFilter === 'completed' && `Vyřízené žádosti (${filteredCompletedTickets.length})`}
                {statusFilter === 'rejected' && `Zamítnuté žádosti (${filteredRejectedTickets.length})`}
                {statusFilter === 'processed' && `Zpracované žádosti (${filteredProcessedTickets.length})`}
              </h2>
              {paginatedTickets.length > 0 ? (
                <>
                  <PropertyMoveTicketList 
                    tickets={paginatedTickets} 
                    currentUserId={user?.id} 
                    onTicketUpdate={loadTicketsData}
                    userRole={user?.role}
                    onStatusChange={handleStatusChange}
                    searchText={searchText}
                  />
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredTickets.length}
                    />
                  )}
                </>
              ) : (
                <p style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Žádné žádosti</p>
              )}
            </div>
          ) : (
            <>
              {newTickets.length > 0 && (
                <div className="tickets-section">
                  <h3>Nové žádosti ({newTickets.length})</h3>
                  <PropertyMoveTicketList 
                    tickets={newTickets} 
                    currentUserId={user?.id} 
                    onTicketUpdate={loadTicketsData}
                    userRole={user?.role}
                    onStatusChange={handleStatusChange}
                    searchText={searchText}
                  />
                </div>
              )}
              
              {inProgressTickets.length > 0 && (
                <div className="tickets-section">
                  <h3>V řešení ({inProgressTickets.length})</h3>
                  <PropertyMoveTicketList 
                    tickets={inProgressTickets} 
                    currentUserId={user?.id} 
                    onTicketUpdate={loadTicketsData}
                    userRole={user?.role}
                    onStatusChange={handleStatusChange}
                    searchText={searchText}
                  />
                </div>
              )}
              
              {completedTickets.length > 0 && (
                <div className="tickets-section">
                  <h3>Dokončené žádosti ({completedTickets.length})</h3>
                  <PropertyMoveTicketList 
                    tickets={completedTickets} 
                    currentUserId={user?.id} 
                    onTicketUpdate={loadTicketsData}
                    userRole={user?.role}
                    onStatusChange={handleStatusChange}
                    searchText={searchText}
                  />
                </div>
              )}
              
              {rejectedTickets.length > 0 && (
                <div className="tickets-section">
                  <h3 style={{ color: '#ef4444' }}>Zamítnuté žádosti ({rejectedTickets.length})</h3>
                  <PropertyMoveTicketList 
                    tickets={rejectedTickets} 
                    currentUserId={user?.id} 
                    onTicketUpdate={loadTicketsData}
                    userRole={user?.role}
                    onStatusChange={handleStatusChange}
                    searchText={searchText}
                  />
                </div>
              )}
              
              {processedTickets.length > 0 && (
                <div className="tickets-section">
                  <h3 style={{ color: '#10b981' }}>Zpracované žádosti ({processedTickets.length})</h3>
                  <PropertyMoveTicketList 
                    tickets={processedTickets} 
                    currentUserId={user?.id} 
                    onTicketUpdate={loadTicketsData}
                    userRole={user?.role}
                    onStatusChange={handleStatusChange}
                    searchText={searchText}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default PropertyReferentView
